import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import { DAMAGE_DOCUMENTS_STEP, DAMAGE_INFO_STEP, DAMAGE_INTRO_TEXTS } from "./consts";
import DamageDocumentsScene from "./scenes/DamageDocumentsScene";
import DamageInfoScene from "./scenes/DamageInfoScene";
import { DamageIntroProps, DamageSceneProps } from "./types";

/** Scena za korak čarobnjaka štete. Srednji korak (fotografije) nema intro. */
const STEP_SCENES: Record<number, React.FC<DamageSceneProps>> = {
  [DAMAGE_INFO_STEP]: DamageInfoScene,
  [DAMAGE_DOCUMENTS_STEP]: DamageDocumentsScene,
};

/**
 * Lijevi panel unosa štete: za korak vlastita scena s karticama koje prikazuju upisane podatke,
 * a ispod objašnjenje koraka. Isti obrazac kao intro čarobnjaka vozila.
 */
const DamageIntro: React.FC<DamageIntroProps> = ({ formValues, stepCurrent, stepCount }) => {
  const stepKey = STEP_SCENES[stepCurrent] ? stepCurrent : DAMAGE_INFO_STEP;
  const Scene = STEP_SCENES[stepKey];
  const text = DAMAGE_INTRO_TEXTS[stepKey];

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={stepKey}>
          <Scene formValues={formValues} />
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
          Korak {stepKey + 1} od {stepCount}
        </div>
        <div key={stepKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{text.title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{text.description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: stepCount }, (_, index) => (
            <span
              key={index}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                index === stepKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(DamageIntro);
